import * as React from 'react'
import { Icon } from '@atoms'
import { ComponentProps } from 'react'
import classnames from 'classnames'
import { FormattedI18nString } from '@/utils/FormattedI18nString'
import { LinkButton } from '@/components'

export type AlertVariantName = 'info' | 'success' | 'error'

export type AlertProps = React.PropsWithChildren & {
    variant?: AlertVariantName
    iconName?: ComponentProps<typeof Icon>['name']
    iconHidden?: boolean
    title?: string
    description?: string | React.ReactNode
    dismissable?: boolean
    linkDescription?: string
    onClickLink?: () => void
    onDismissClick?: () => void
}

type AlertVariantClassnames = {
    default: string
    iconClassname: string
    iconName: string
}

export const Alert: React.FC<AlertProps> = (props) => {
    const {
        title,
        description,
        dismissable,
        iconHidden,
        onDismissClick,
        linkDescription,
        onClickLink,
    } = props
    const variantName: AlertVariantName = props.variant ?? 'info'

    const variants: Record<AlertVariantName, AlertVariantClassnames> = {
        info: {
            default: 'bg-blue-50 border-blue-200',
            iconClassname: 'text-azure',
            iconName: 'info',
        },
        success: {
            default: 'bg-green-50 border-green-200',
            iconClassname: 'text-green-500',
            iconName: 'task_alt',
        },
        error: {
            default: 'bg-red-50 border-red-200',
            iconClassname: 'text-red-500',
            iconName: 'error',
        },
    }

    const variant = variants[variantName]

    const className = classnames(
        'relative p-4 rounded-xl border',
        variant.default
    )

    const iconName = props.iconName ?? variant.iconName

    return (
        <div
            className={className}
            data-testid={`alert-container-${variant.iconName}`}
        >
            <div className="flex gap-2">
                {!iconHidden && (
                    <Icon
                        className={variant.iconClassname}
                        name={iconName}
                        data-testid="alert-icon"
                    />
                )}
                <div className="space-y-1 text-regular">
                    {title && (
                        <div
                            className="text-heading-6"
                            data-testid="alert-title"
                        >
                            {title}
                        </div>
                    )}
                    {description && (
                        <>
                            <div
                                className="space-y-2 text-sm"
                                data-testid="alert-description"
                            >
                                {typeof description === 'string' ? (
                                    <FormattedI18nString
                                        i18nKey={description}
                                    />
                                ) : (
                                    description
                                )}
                            </div>
                            {linkDescription && onClickLink && (
                                <LinkButton
                                    variant="primary"
                                    name={linkDescription}
                                    label={linkDescription}
                                    onClick={onClickLink}
                                />
                            )}
                        </>
                    )}
                </div>
            </div>
            {dismissable && (
                <button
                    className={'absolute right-4 top-4 text-neutral-500'}
                    onClick={onDismissClick}
                    data-testid="alert-button"
                >
                    <Icon name="close" />
                </button>
            )}
        </div>
    )
}
